/**
 * `/dashboard` — landing page after sign-in (RLY-024).
 *
 * AC #1: lists recent runs (max 10) with status badge, agent name, cost,
 * duration. Empty state when no runs exist.
 *
 * Shows per-module summary cards for every module the user is entitled to
 * (Agents always; Leads and Content gated on publicMetadata.modules).
 */
import { useAuth, useUser } from "@clerk/clerk-react";
import { useQuery } from "@tanstack/react-query";
import type { ContentPostListResponse, LeadAnalyticsResponse } from "@simpliwork/api-schema";
import { Link } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { OnboardingHero } from "@/components/onboarding-hero";
import { Skeleton } from "@/components/ui/skeleton";
import { contentKeys } from "@/hooks/use-content";
import { useAgentsList } from "@/hooks/use-agents";
import { leadsKeys } from "@/hooks/use-leads";
import { useQuoteDocuments } from "@/hooks/use-quote";
import { useFunnelAnalytics } from "@/hooks/use-analytics";
import { useMe } from "@/hooks/use-me";
import { useRunsList } from "@/hooks/use-runs";
import type { ApiError } from "@/lib/api-client";
import { fetchLeadsAnalytics, fetchPosts } from "@/lib/api-client";
import {
  formatCostMicros,
  formatDurationMs,
  formatTimestamp,
  runDurationMs,
  runStatusToBadgeVariant,
} from "@/lib/formatters";
import { hasModuleAccess } from "@/lib/modules";

export function DashboardPage() {
  const { user } = useUser();
  const { getToken } = useAuth();
  const me = useMe();
  const runs = useRunsList({ limit: 10 });
  const agents = useAgentsList({ limit: 50 });

  const hasLeadsAccess = hasModuleAccess(user?.publicMetadata, "leads");
  const hasContentAccess = hasModuleAccess(user?.publicMetadata, "content");
  const hasQuoteAccess = hasModuleAccess(user?.publicMetadata, "quote");

  const quoteDocs = useQuoteDocuments();
  const quoteDocItems = quoteDocs.data?.items ?? [];
  const quoteIndexedCount = quoteDocItems.filter((d) => d.status === "ready").length;
  const quoteInFlightCount = quoteDocItems.filter(
    (d) => d.status === "pending" || d.status === "processing",
  ).length;

  const funnelAnalytics = useFunnelAnalytics({ enabled: hasContentAccess });

  const leadsAnalytics = useQuery<LeadAnalyticsResponse, ApiError | Error>({
    queryKey: leadsKeys.analytics(),
    queryFn: () => fetchLeadsAnalytics({ getToken: () => getToken() }),
    enabled: hasLeadsAccess,
    staleTime: 30_000,
  });

  const scheduledPosts = useQuery<ContentPostListResponse, ApiError | Error>({
    queryKey: contentKeys.posts({ status: "scheduled" }),
    queryFn: () => fetchPosts({ status: "scheduled" }, { getToken: () => getToken() }),
    enabled: hasContentAccess,
    staleTime: 30_000,
  });

  const newLeads = leadsAnalytics.data?.status_counts["new"] ?? 0;
  const totalLeads = leadsAnalytics.data?.total ?? 0;
  const inProgressLeads =
    (leadsAnalytics.data?.status_counts["contacted"] ?? 0) +
    (leadsAnalytics.data?.status_counts["qualified"] ?? 0);

  const scheduledCount = scheduledPosts.data?.items.length ?? 0;
  const nextPost = scheduledPosts.data?.items
    .filter((p) => p.publish_at)
    .sort((a, b) => new Date(a.publish_at!).getTime() - new Date(b.publish_at!).getTime())[0] ?? null;

  // Derive funnel summary totals from by_post rollup
  const funnelByPost = funnelAnalytics.data?.by_post ?? [];
  const funnelSummary = funnelByPost.reduce(
    (acc, item) => ({
      total_clicks: acc.total_clicks + item.clicks,
      total_leads: acc.total_leads + item.leads,
      total_bookings: acc.total_bookings + item.booked,
    }),
    { total_clicks: 0, total_leads: 0, total_bookings: 0 },
  );
  const topPosts = funnelByPost.slice(0, 5);

  return (
    <div className="flex flex-col gap-8">
      <header>
        <p className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-primary">
          Dashboard
        </p>
        <h1
          className="text-3xl font-extrabold tracking-tight text-gray-900"
          data-testid="dashboard-heading"
        >
          Welcome
          {me.data ? (
            <>, {me.data.user.display_name ?? me.data.user.email.split("@")[0]}</>
          ) : null}
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          {me.data ? (
            <>
              Workspace:{" "}
              <span
                data-testid="dashboard-tenant-name"
                className="font-semibold text-foreground"
              >
                {me.data.tenant.name}
              </span>
            </>
          ) : (
            <span data-testid="dashboard-tenant-loading">Loading workspace…</span>
          )}
        </p>
      </header>

      {/* Onboarding hero — adaptive next-step CTA based on tenant state.
          Renders nothing once the tenant is subscribed + has agents + has runs. */}
      <OnboardingHero />

      {/* ── Module summary cards ─────────────────────────────────────────── */}
      <section className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
        {/* Agents */}
        <Card data-testid="agents-summary-card">
          <CardHeader>
            <CardTitle>Agents</CardTitle>
            <CardDescription>Configure agents to run against your data.</CardDescription>
          </CardHeader>
          <CardContent>
            {agents.isLoading ? (
              <Skeleton className="h-10 w-32" data-testid="agents-summary-skeleton" />
            ) : agents.error ? (
              <p data-testid="agents-summary-error" className="text-sm text-destructive">
                Couldn't load agents: {agents.error.message}
              </p>
            ) : (
              <div className="flex items-end justify-between gap-4">
                <div>
                  <p className="text-3xl font-semibold" data-testid="agents-count">
                    {agents.data?.items.length ?? 0}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {(agents.data?.items.length ?? 0) === 1 ? "agent" : "agents"} configured
                  </p>
                </div>
                <Link to="/agents">
                  <Button variant="outline" size="sm" data-testid="agents-summary-cta">
                    View agents
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Leads — only when entitled */}
        {hasLeadsAccess && (
          <Card data-testid="leads-summary-card">
            <CardHeader>
              <CardTitle>Leads</CardTitle>
              <CardDescription>Inbound leads across all sources.</CardDescription>
            </CardHeader>
            <CardContent>
              {leadsAnalytics.isLoading ? (
                <Skeleton className="h-10 w-full" data-testid="leads-summary-skeleton" />
              ) : leadsAnalytics.error ? (
                <p data-testid="leads-summary-error" className="text-sm text-destructive">
                  Couldn't load leads: {leadsAnalytics.error.message}
                </p>
              ) : (
                <div className="flex flex-col gap-3">
                  <div className="flex items-end justify-between gap-4">
                    <div>
                      <p className="text-3xl font-semibold" data-testid="leads-total-count">
                        {totalLeads}
                      </p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        <span
                          className="font-semibold text-amber-600"
                          data-testid="leads-new-count"
                        >
                          {newLeads} new
                        </span>
                        {inProgressLeads > 0 && (
                          <> · {inProgressLeads} in progress</>
                        )}
                      </p>
                    </div>
                    <Link to="/leads">
                      <Button variant="outline" size="sm" data-testid="leads-summary-cta">
                        View leads
                      </Button>
                    </Link>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Quote — only when entitled */}
        {hasQuoteAccess && (
          <Card data-testid="quote-summary-card">
            <CardHeader>
              <CardTitle>Quote</CardTitle>
              <CardDescription>AI quote desk over your uploaded documents.</CardDescription>
            </CardHeader>
            <CardContent>
              {quoteDocs.isLoading ? (
                <Skeleton className="h-10 w-full" data-testid="quote-summary-skeleton" />
              ) : quoteDocs.error ? (
                <p data-testid="quote-summary-error" className="text-sm text-destructive">
                  Couldn't load quote: {quoteDocs.error.message}
                </p>
              ) : (
                <div className="flex items-end justify-between gap-4">
                  <div>
                    <p className="text-3xl font-semibold" data-testid="quote-indexed-count">
                      {quoteIndexedCount}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {quoteIndexedCount === 1 ? "document" : "documents"} indexed
                      {quoteInFlightCount > 0 && (
                        <>
                          {" · "}
                          <span
                            className="font-semibold text-amber-600"
                            data-testid="quote-inflight-count"
                          >
                            {quoteInFlightCount} processing
                          </span>
                        </>
                      )}
                    </p>
                  </div>
                  <Link to="/quote/documents">
                    <Button variant="outline" size="sm" data-testid="quote-summary-cta">
                      {quoteIndexedCount === 0 ? "Upload first doc" : "View quote"}
                    </Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Content — only when entitled */}
        {hasContentAccess && (
          <Card data-testid="content-summary-card">
            <CardHeader>
              <CardTitle>Content</CardTitle>
              <CardDescription>Scheduled and published social posts.</CardDescription>
            </CardHeader>
            <CardContent>
              {scheduledPosts.isLoading ? (
                <Skeleton className="h-10 w-full" data-testid="content-summary-skeleton" />
              ) : scheduledPosts.error ? (
                <p data-testid="content-summary-error" className="text-sm text-destructive">
                  Couldn't load content: {scheduledPosts.error.message}
                </p>
              ) : (
                <div className="flex items-end justify-between gap-4">
                  <div>
                    <p className="text-3xl font-semibold" data-testid="content-scheduled-count">
                      {scheduledCount}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {scheduledCount === 1 ? "post" : "posts"} scheduled
                      {nextPost?.publish_at && (
                        <>
                          {" · "}
                          <span data-testid="content-next-post-time">
                            next {formatTimestamp(nextPost.publish_at)}
                          </span>
                        </>
                      )}
                    </p>
                  </div>
                  <Link to="/content">
                    <Button variant="outline" size="sm" data-testid="content-summary-cta">
                      View content
                    </Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </section>

      {/* ── Quick actions ────────────────────────────────────────────────── */}
      <section className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <Card data-testid="quick-actions-card">
          <CardHeader>
            <CardTitle>Quick actions</CardTitle>
            <CardDescription>Get started with your first agent.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            <Link to="/agents/new">
              <Button data-testid="quick-action-new-agent" className="w-full sm:w-auto">
                Create your first agent
              </Button>
            </Link>
            <Link to="/agents">
              <Button
                variant="outline"
                data-testid="quick-action-browse-agents"
                className="w-full sm:w-auto"
              >
                Browse all agents
              </Button>
            </Link>
          </CardContent>
        </Card>
      </section>

      {/* ── Funnel — only when content module is entitled ───────────────── */}
      {hasContentAccess && (
        <section data-testid="funnel-section">
          <p className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-primary">
            Content Funnel
          </p>

          {/* Summary stat cards */}
          {funnelAnalytics.isLoading ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 mb-6">
              <Skeleton className="h-24 rounded-xl" data-testid="funnel-skeleton-clicks" />
              <Skeleton className="h-24 rounded-xl" data-testid="funnel-skeleton-leads" />
              <Skeleton className="h-24 rounded-xl" data-testid="funnel-skeleton-bookings" />
            </div>
          ) : funnelAnalytics.error ? (
            <p data-testid="funnel-error" className="mb-6 text-sm text-destructive">
              Couldn't load funnel data: {funnelAnalytics.error.message}
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 mb-6">
              <Card data-testid="funnel-stat-clicks">
                <CardHeader className="pb-1 pt-4 px-4">
                  <CardTitle className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Total Clicks
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-4">
                  <p className="text-3xl font-bold text-gray-900" data-testid="funnel-clicks-value">
                    {funnelSummary.total_clicks}
                  </p>
                </CardContent>
              </Card>
              <Card data-testid="funnel-stat-leads">
                <CardHeader className="pb-1 pt-4 px-4">
                  <CardTitle className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Leads from Content
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-4">
                  <p className="text-3xl font-bold text-gray-900" data-testid="funnel-leads-value">
                    {funnelSummary.total_leads}
                  </p>
                </CardContent>
              </Card>
              <Card data-testid="funnel-stat-bookings">
                <CardHeader className="pb-1 pt-4 px-4">
                  <CardTitle className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Bookings from Content
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-4">
                  <p className="text-3xl font-bold text-gray-900" data-testid="funnel-bookings-value">
                    {funnelSummary.total_bookings}
                  </p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Top performing posts table */}
          {!funnelAnalytics.isLoading && !funnelAnalytics.error && topPosts.length > 0 && (
            <Card data-testid="funnel-top-posts-card">
              <CardHeader>
                <CardTitle>Top Performing Posts</CardTitle>
                <CardDescription>Up to 5 posts ranked by attributed leads.</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Table header */}
                <div className="hidden sm:grid grid-cols-[1fr_80px_80px_80px] gap-3 px-2 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground border-b border-gray-100">
                  <span>Post</span>
                  <span className="text-right">Clicks</span>
                  <span className="text-right">Leads</span>
                  <span className="text-right">Bookings</span>
                </div>
                <ul className="divide-y">
                  {topPosts.map((item) => (
                    <li
                      key={item.post_id}
                      className="py-3"
                      data-testid={`funnel-post-row-${item.post_id}`}
                    >
                      {/* Desktop row */}
                      <div className="hidden sm:grid grid-cols-[1fr_80px_80px_80px] gap-3 items-center">
                        <div className="flex flex-col gap-0.5 min-w-0">
                          <span className="text-xs font-semibold uppercase text-gray-500">
                            {item.platform}
                          </span>
                          <p className="text-sm text-gray-900 truncate max-w-sm">
                            {item.body_preview}
                          </p>
                        </div>
                        <p className="text-right text-sm font-semibold text-gray-900">
                          {item.clicks}
                        </p>
                        <p className="text-right text-sm font-semibold text-gray-900">
                          {item.leads}
                        </p>
                        <p className="text-right text-sm font-semibold text-gray-900">
                          {item.booked}
                        </p>
                      </div>
                      {/* Mobile row */}
                      <div className="flex items-start justify-between gap-3 sm:hidden">
                        <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                          <span className="text-xs font-semibold uppercase text-gray-500">
                            {item.platform}
                          </span>
                          <p className="text-sm text-gray-900 line-clamp-2">{item.body_preview}</p>
                        </div>
                        <div className="shrink-0 flex gap-3 text-right">
                          <div>
                            <p className="text-sm font-semibold text-gray-900">{item.clicks}</p>
                            <p className="text-xs text-muted-foreground">clicks</p>
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-900">{item.leads}</p>
                            <p className="text-xs text-muted-foreground">leads</p>
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-900">{item.booked}</p>
                            <p className="text-xs text-muted-foreground">booked</p>
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {!funnelAnalytics.isLoading && !funnelAnalytics.error && topPosts.length === 0 && (
            <p className="text-sm text-muted-foreground" data-testid="funnel-empty">
              No published posts with tracked links yet.
            </p>
          )}
        </section>
      )}

      {/* ── Recent runs ──────────────────────────────────────────────────── */}
      <section>
        <Card data-testid="recent-runs-card">
          <CardHeader>
            <CardTitle>Recent runs</CardTitle>
            <CardDescription>Last 10 runs across all agents in this workspace.</CardDescription>
          </CardHeader>
          <CardContent>
            {runs.isLoading && (
              <div className="flex flex-col gap-2" data-testid="runs-loading">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            )}
            {runs.error && (
              <p data-testid="runs-error" className="text-sm text-destructive">
                Couldn't load recent runs: {runs.error.message}
              </p>
            )}
            {runs.data && runs.data.items.length === 0 && (
              <div data-testid="runs-empty" className="flex flex-col items-start gap-3">
                <p className="text-sm text-muted-foreground">
                  No runs yet — trigger one from the agent detail page.
                </p>
                <Link to="/agents">
                  <Button variant="outline" size="sm">
                    Pick an agent
                  </Button>
                </Link>
              </div>
            )}
            {runs.data && runs.data.items.length > 0 && (
              <ul className="divide-y" data-testid="runs-list">
                {runs.data.items.map((run) => {
                  const duration = runDurationMs({
                    started_at: run.started_at,
                    completed_at: run.completed_at,
                  });
                  return (
                    <li
                      key={run.id}
                      className="flex flex-wrap items-center justify-between gap-3 py-3"
                      data-testid={`run-row-${run.id}`}
                    >
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">Run {run.id.slice(0, 8)}</span>
                        <span className="text-xs text-muted-foreground">
                          Agent {run.agent_id.slice(0, 8)} · v{run.agent_version} ·{" "}
                          {formatTimestamp(run.created_at)}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-xs">
                        <span data-testid={`run-cost-${run.id}`}>
                          {formatCostMicros(run.usage.cost_micros)}
                        </span>
                        <span data-testid={`run-duration-${run.id}`}>
                          {formatDurationMs(duration)}
                        </span>
                        <Badge
                          variant={runStatusToBadgeVariant(run.status)}
                          data-testid={`run-status-${run.id}`}
                        >
                          {run.status}
                        </Badge>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>
      </section>
    </div>
  );
}

export default DashboardPage;
